/**
 * TranscodingStatusCtrl — content-level transcoding overview (content-delivery streams API).
 */

import {
  streamDeliveryStatusCssClass,
  transcodingStatusDisplayText,
  transcodingStatusStateCssClass
} from 'app/helpers/streamWorkState'

const CONTENT_TYPE_BY_TAB = {
  channels: 'CHANNEL',
  liveEvents: 'LIVE_EVENT',
  vods: 'VOD',
  episodes: 'EPISODE'
}

const VIEW_ACCESS_BY_CONTENT_TYPE = {
  CHANNEL: 'viewChannel',
  LIVE_EVENT: 'viewLiveEvent',
  VOD: 'viewVod',
  EPISODE: 'viewVodSeries'
}

const SEARCH_PLACEHOLDER_BY_TAB = {
  channels: 'Search by Channel Name, Transcoder Name',
  liveEvents: 'Search by Event Name, Transcoder Name',
  vods: 'Search by VOD Name, Transcoder Name',
  episodes: 'Search by TV Show Name, Transcoder Name'
}

const STREAM_STATUS_LABELS = {
  ENABLED: 'Enabled',
  DISABLED: 'Disabled',
  SCHEDULED: 'Scheduled'
}

const STREAM_STATUS_FILTER_VALUES = Object.freeze(['ENABLED', 'DISABLED', 'SCHEDULED'])

/** GET `stream-state` filter (single value) */
const STREAM_STATE_FILTER_VALUES = Object.freeze([
  'DONE',
  'ERROR',
  'NOT_AVAILABLE',
  'PROCESSING',
  'TRANSITION',
  'WAITING'
])

const STREAM_STATUS_POLL_MS = 10000

/** Smart-table predicate -> GET sort-by */
const SORT_PREDICATE_TO_API = {
  contentName: 'contentName',
  streamEnabled: 'stream-status',
  organizationCount: 'organization-count',
  setrixServerName: 'server-name',
  // Column predicates in `transcodingStatus.html` must be sent as-is for API sorting.
  streamName: 'streamName',
  streamId: 'streamId',
  // Backward compatibility (older predicate names used in code)
  transcoderName: 'streamName',
  transcoderId: 'streamId',
  workStatus: 'stream-state'
}

function sortFieldForApi (predicate) {
  if (!predicate) return 'content-name'
  if (SORT_PREDICATE_TO_API[predicate]) return SORT_PREDICATE_TO_API[predicate]
  return String(predicate)
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, '')
}

function buildStreamsOverviewParams (tableState, contentTypeKey) {
  const pageSize = tableState.pagination.number || 25
  const page = Math.floor((tableState.pagination.start || 0) / pageSize) || 0
  const qRaw = tableState.search.predicateObject && tableState.search.predicateObject.q
  const q = (qRaw != null && String(qRaw).trim()) ? String(qRaw).trim() : undefined

  const streamStatusRaw = tableState.search.predicateObject && tableState.search.predicateObject.streamStatuses
  const streamStatusNorm = streamStatusRaw != null && String(streamStatusRaw).trim()
    ? String(streamStatusRaw).trim().toUpperCase()
    : ''

  const streamStateRaw = tableState.search.predicateObject && tableState.search.predicateObject.streamStates
  const streamStateNorm = streamStateRaw != null && String(streamStateRaw).trim()
    ? String(streamStateRaw).trim().toUpperCase()
    : ''

  const serverIdsRaw = tableState.search.predicateObject && tableState.search.predicateObject.serverIds
  const serverIdsJoined = Array.isArray(serverIdsRaw) && serverIdsRaw.length
    ? serverIdsRaw.map((id) => String(id).trim()).filter(Boolean).join(',')
    : ''

  const networkRaw = tableState.search.predicateObject && tableState.search.predicateObject.network
  const networkIdsJoined = Array.isArray(networkRaw) && networkRaw.length
    ? networkRaw.map((id) => String(id).trim()).filter(Boolean).join(',')
    : ''

  const params = {
    page,
    count: pageSize,
    'sort-by': sortFieldForApi(tableState.sort.predicate),
    'sort-order': tableState.sort.reverse ? 'DESC' : 'ASC',
    'content-type': CONTENT_TYPE_BY_TAB[contentTypeKey] || 'CHANNEL'
  }

  if (q) params.q = q
  if (streamStatusNorm && STREAM_STATUS_FILTER_VALUES.includes(streamStatusNorm)) {
    params['stream-status'] = streamStatusNorm
  }
  if (streamStateNorm && STREAM_STATE_FILTER_VALUES.includes(streamStateNorm)) {
    params['stream-state'] = streamStateNorm
  }
  if (serverIdsJoined) {
    params.serverIds = serverIdsJoined
  }
  if (networkIdsJoined) {
    params.networkIds = networkIdsJoined
  }

  return params
}

function organizationCountFromItem (item) {
  if (Array.isArray(item.organizations)) {
    return item.organizations.length
  }
  const n = item.organizationCount != null
    ? Number(item.organizationCount)
    : (item.organizationsCount != null ? Number(item.organizationsCount) : NaN)
  return Number.isFinite(n) ? n : 0
}

function pickFirstNonEmptyString (...candidates) {
  for (let i = 0; i < candidates.length; i++) {
    const v = candidates[i]
    if (v == null) continue
    const s = String(v).trim()
    if (s !== '') return s
  }
  return ''
}

/**
 * TV show title from streams overview item (episode rows).
 * Supports camelCase and snake_case field names and nested tvShow.
 */
function tvShowNameFromOverviewItem (item) {
  if (!item || typeof item !== 'object') return ''
  return pickFirstNonEmptyString(
    item.tvShowName,
    item.showName,
    item.tv_show_name,
    item.show_name,
    item.parentContentName,
    item.parent_content_name,
    item.tvShow && item.tvShow.name,
    item.show && item.show.name
  )
}

/**
 * Season label for overview row; empty if episode has no season in API payload.
 */
function seasonLabelFromOverviewItem (item) {
  if (!item || typeof item !== 'object') return ''
  const direct = pickFirstNonEmptyString(item.seasonName, item.season_name)
  if (direct) return direct
  const s = item.season
  if (s && typeof s === 'object') {
    const num = s.displayNumber != null && String(s.displayNumber).trim() !== ''
      ? String(s.displayNumber).trim()
      : ''
    const name = pickFirstNonEmptyString(s.name)
    if (num && name) return `${num}. ${name}`
    if (name) return name
    if (num) return `Season ${num}`
  }
  return ''
}

function mapOverviewItemToRow (item) {
  const orgs = Array.isArray(item.organizations) ? item.organizations : []
  const organizationCount = Array.isArray(item.organizations)
    ? item.organizations.length
    : organizationCountFromItem(item)

  /* streamStatus / enable flag may be omitted from overview; filled via Stream.loadStatuses */
  const streamStatusNorm = item.streamStatus != null ? String(item.streamStatus).toUpperCase().trim() : ''
  const streamStateNorm = item.streamState != null ? String(item.streamState).toUpperCase().trim() : ''

  return {
    id: item.id,
    contentId: item.contentId,
    contentType: item.contentType,
    contentName: item.contentName || '',
    tvShowId: item.tvShowId || item.showId || item.tv_show_id || item.show_id || item.tvShow?.id || item.show?.id || null,
    seasonId: item.seasonId || item.season_id || item.season?.id || null,
    tvShowName: tvShowNameFromOverviewItem(item),
    seasonLabel: seasonLabelFromOverviewItem(item),
    streamStatus: streamStatusNorm,
    streamEnabled: streamStatusNorm !== '' ? streamStatusNorm === 'ENABLED' : false,
    organizationCount,
    setrixServerName: item.serverName || '',
    transcoderName: item.streamName || '',
    transcoderId: item.streamId || '',
    workStatus: streamStateNorm,
    statusState: streamStateNorm,
    serverId: item.serverId,
    serverStatus: item.serverStatus,
    organizations: orgs,
    raw: item
  }
}

/* @ngInject */
export default function TranscodingStatusCtrl ($scope, $timeout, $window, SetrixServer, Stream, alertLiteSvc, authSvc, InappSettings) {
  const vm = this
  let streamStatusesPollingId = null

  function overviewRequestContext () {
    let multiorgEnabled = !!InappSettings?._current?.multiorgEnabled
    const networkId = authSvc?.getUser?.()?.network?.id ?? null
    if (multiorgEnabled && networkId == null) {
      multiorgEnabled = false
    }
    return { networkId, multiorgEnabled }
  }

  vm.tabs = [
    { key: 'channels', label: 'Channels', pageTitle: 'Channels', iconClass: 'far fa-film' },
    { key: 'liveEvents', label: 'Live Events', pageTitle: 'Live Events', iconClass: 'fas fa-record-vinyl' },
    { key: 'vods', label: 'VODs', pageTitle: 'VODs', iconClass: 'far fa-video' },
    { key: 'episodes', label: 'TV Show Episodes', pageTitle: 'TV Show Episodes', iconClass: 'far fa-photo-video' }
  ]

  vm.contentType = 'channels'
  vm.rows = []
  vm.isFound = false
  vm.setrixServerFilterList = []
  vm.organizationFilterList = []

  vm.stopEvent = function ($event) {
    if ($event) {
      $event.stopPropagation()
      $event.preventDefault()
    }
  }

  $scope.loading = true

  vm.currentPageTitle = function () {
    const tab = vm.tabs.find(t => t.key === vm.contentType)
    return tab ? tab.pageTitle : ''
  }

  vm.searchPlaceholder = function () {
    return SEARCH_PLACEHOLDER_BY_TAB[vm.contentType] || SEARCH_PLACEHOLDER_BY_TAB.channels
  }

  /**
   * Display count for Organizations column — always derived from organizations[] when present.
   */
  vm.organizationCount = function (row) {
    if (!row) return 0
    if (Array.isArray(row.organizations)) {
      return row.organizations.length
    }
    const n = row.organizationCount
    if (typeof n === 'number' && !Number.isNaN(n)) return n
    if (row.raw) return organizationCountFromItem(row.raw)
    return 0
  }

  vm.hasOrganizationList = function (row) {
    return Boolean(row && Array.isArray(row.organizations) && row.organizations.length > 0)
  }

  vm.organizationDisplayName = function (org) {
    if (org == null) return ''
    if (typeof org === 'string') return org
    if (typeof org === 'object') {
      if (org.name != null) return String(org.name)
      if (org.organizationName != null) return String(org.organizationName)
      if (org.title != null) return String(org.title)
    }
    return String(org)
  }

  vm.organizationSortKey = function (org) {
    return vm.organizationDisplayName(org).toLowerCase()
  }

  vm.organizationTooltipAriaLabel = function (row) {
    const n = vm.organizationCount(row)
    if (n === 1) return '1 organization, open list'
    return `${n} organizations, open list`
  }

  /** Table body colspan (single Content Name column for all tabs) */
  vm.transcodingStatusTableColspan = function () {
    return vm.hasTranscoderActionsColumnAccess() ? 7 : 6
  }

  vm.hasTranscoderActionsColumnAccess = function () {
    return !!(
      authSvc?.hasAccess?.('modifyAssignmentTranscoder') ||
      authSvc?.hasAccess?.('runtimeControlTranscoder')
    )
  }

  vm.setContentType = function (key) {
    if (vm.contentType === key) return
    vm.contentType = key
    vm.rows = []
    vm.isFound = false
    stopStreamStatusesPolling()
    $scope.loading = true
    $timeout(() => {
      $scope.$broadcast('refreshTable', { resetState: true })
    })
  }

  function loadSetrixServersForFilter () {
    SetrixServer.loadStreamsExtra()
      .then((extra) => {
        vm.setrixServerFilterList = extra.serverFilterList
        vm.organizationFilterList = extra.organizationFilterList || []
      })
      .catch(() => {
        vm.setrixServerFilterList = []
        vm.organizationFilterList = []
      })
  }

  loadSetrixServersForFilter()

  function stopStreamStatusesPolling () {
    if (streamStatusesPollingId) {
      clearInterval(streamStatusesPollingId)
      streamStatusesPollingId = null
    }
  }

  function startStreamStatusesPolling () {
    if (streamStatusesPollingId) return
    streamStatusesPollingId = setInterval(() => {
      if (!Array.isArray(vm.rows) || !vm.rows.length) return
      if (!Stream.buildAssignedTranscodersFromOverviewRows(vm.rows).length) return
      applyStreamStatusesToRows(vm.rows, { skipLoadingFlag: true })
    }, STREAM_STATUS_POLL_MS)
  }

  function syncStreamStatusesPollingAfterLoad () {
    const canPoll = Array.isArray(vm.rows) &&
      vm.rows.length > 0 &&
      Stream.buildAssignedTranscodersFromOverviewRows(vm.rows).length > 0
    if (canPoll) {
      startStreamStatusesPolling()
    } else {
      stopStreamStatusesPolling()
    }
  }

  function applyStreamStatusesToRows (rows, options = {}) {
    const skipLoadingFlag = !!options.skipLoadingFlag
    if (!Array.isArray(rows) || !rows.length) return Promise.resolve()

    const params = Stream.buildAssignedTranscodersFromOverviewRows(rows)
    if (!skipLoadingFlag) {
      rows.forEach((row) => {
        const ok = row.serverId != null && row.transcoderId != null && row.transcoderId !== ''
        row._streamStatusLoading = !!(ok && params.length)
      })
    }

    if (!params.length) {
      if (!skipLoadingFlag) {
        rows.forEach((row) => { row._streamStatusLoading = false })
      }
      return Promise.resolve()
    }

    return Stream.loadStatuses(null, params, { silent: true })
      .then((items) => {
        const statusItems = Array.isArray(items) ? items : []
        const statusesByExternalId = new Map(
          statusItems
            .filter((it) => it?.externalId)
            .map((it) => [String(it.externalId), it])
        )
        rows.forEach((row) => {
          const matched = statusesByExternalId.get(String(row.transcoderId))
          if (matched) {
            const editUrlRaw = matched.editUrl
            if (editUrlRaw != null && String(editUrlRaw).trim() !== '') {
              row.editUrl = String(editUrlRaw).trim()
            }
          }
          if (row._streamToggleInProgress) return
          if (!matched) {
            delete row.statusLabel
            return
          }

          const activeFlag = matched?.status?.active
          if (activeFlag !== undefined && activeFlag !== null) {
            row.streamEnabled = !!activeFlag
          }

          const statusStateRaw = matched?.status?.statusState
          if (statusStateRaw != null && String(statusStateRaw).trim() !== '') {
            const stateNorm = String(statusStateRaw).toUpperCase().trim()
            if (stateNorm) {
              row.workStatus = stateNorm
              row.statusState = stateNorm
            }
          }

          const statusLabelRaw = matched?.status?.statusLabel
          if (statusLabelRaw != null && String(statusLabelRaw).trim() !== '') {
            row.statusLabel = String(statusLabelRaw).trim()
          } else {
            delete row.statusLabel
          }

          // Delivery/binding status (ENABLED | DISABLED | SCHEDULED) — only from explicit API fields.
          // `active` drives the start/stop toggle (streamEnabled) only, not this column.
          const deliveryRaw = matched?.status?.streamStatus ?? matched?.streamStatus
          const deliveryNorm = deliveryRaw != null ? String(deliveryRaw).toUpperCase().trim() : ''
          if (deliveryNorm && STREAM_STATUS_FILTER_VALUES.includes(deliveryNorm)) {
            row.streamStatus = deliveryNorm
          }
        })
      })
      .finally(() => {
        if (!skipLoadingFlag) {
          rows.forEach((row) => { row._streamStatusLoading = false })
        }
      })
  }

  vm.streamStatusLabel = function (row) {
    const s = row && row.streamStatus
    if (s == null || s === '') return '—'
    const key = String(s).toUpperCase().trim()
    return STREAM_STATUS_LABELS[key] || s
  }

  vm.streamStatusClass = function (row) {
    return streamDeliveryStatusCssClass(row && row.streamStatus)
  }

  vm.workStatusClass = function (row) {
    const state = row && row.statusState
    if (state == null || state === '') return 'text-muted'
    return transcodingStatusStateCssClass(state)
  }

  vm.workStatusDisplay = function (row) {
    if (!row) return '—'
    return transcodingStatusDisplayText(row.statusLabel)
  }

  vm.contentItemUrl = function (row) {
    if (!row || !row.contentId) return null
    const ct = String(row.contentType || '').toUpperCase()
    const requiredAccess = VIEW_ACCESS_BY_CONTENT_TYPE[ct]
    if (!requiredAccess) return null
    if (!authSvc?.hasAccess?.(requiredAccess)) return null
    if (ct === 'CHANNEL') return `channels/${row.contentId}`
    if (ct === 'LIVE_EVENT') return `live-events/${row.contentId}`
    if (ct === 'VOD') return `vods/${row.contentId}`
    if (ct === 'EPISODE') {
      if (!row.tvShowId) return null
      const seasonPart = row.seasonId != null ? String(row.seasonId) : 'none'
      return `shows/${row.tvShowId}/seasons/${seasonPart}/episodes/${row.contentId}`
    }
    return null
  }

  vm.onStreamEnabledChange = function (row) {
    if (!row || row._streamToggleInProgress) return
    if (!authSvc?.hasAccess?.('runtimeControlTranscoder')) {
      alertLiteSvc.alertError('Permission denied')
      row.streamEnabled = !row.streamEnabled
      return
    }

    const serverId = row.serverId
    const streamId = row.transcoderId
    const wantEnabled = !!row.streamEnabled

    if (!serverId || streamId == null || streamId === '') {
      alertLiteSvc.alertError('Setrix server or stream is missing')
      row.streamEnabled = !wantEnabled
      return
    }

    row._streamToggleInProgress = true
    const { networkId, multiorgEnabled } = overviewRequestContext()
    const request = wantEnabled
      ? SetrixServer.startStream(serverId, streamId, networkId, multiorgEnabled)
      : SetrixServer.stopStream(serverId, streamId, networkId, multiorgEnabled)

    let toggleSucceeded = false
    request
      .then(() => {
        toggleSucceeded = true
        alertLiteSvc.alert(`Transcoder ${wantEnabled ? 'started' : 'stopped'} successfully`, 'success')
      })
      .catch(() => {
        row.streamEnabled = !wantEnabled
        alertLiteSvc.alertError(`Failed to ${wantEnabled ? 'start' : 'stop'} transcoder`)
      })
      .finally(() => {
        row._streamToggleInProgress = false
        if (toggleSucceeded) {
          applyStreamStatusesToRows(vm.rows, { skipLoadingFlag: true })
        }
      })
  }

  vm.refreshRow = function (row) {
    if (!row || row._restartInProgress) return
    if (!authSvc?.hasAccess?.('runtimeControlTranscoder')) {
      alertLiteSvc.alertError('Permission denied')
      return
    }

    const serverId = row.serverId
    const streamId = row.transcoderId
    if (!serverId || streamId == null || streamId === '') {
      alertLiteSvc.alertError('Setrix server or stream is missing')
      return
    }

    row._restartInProgress = true
    const { networkId, multiorgEnabled } = overviewRequestContext()
    SetrixServer.restartStream(serverId, streamId, networkId, multiorgEnabled)
      .then(() => {
        alertLiteSvc.alert('Transcoder restarted successfully', 'success')
        applyStreamStatusesToRows(vm.rows, { skipLoadingFlag: true })
      })
      .catch((rejection) => {
        alertLiteSvc.alertError(rejection?.data?.message || rejection?.statusText || 'Failed to restart stream')
      })
      .finally(() => {
        row._restartInProgress = false
      })
  }

  vm.openExternal = function (row) {
    if (!authSvc?.hasAccess?.('runtimeControlTranscoder')) {
      alertLiteSvc.alertError('Permission denied')
      return
    }
    const url = row && (row.editUrl || row.externalUrl)
    if (url) {
      $window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      alertLiteSvc.alert('No external link for this row.', 'warning')
    }
  }

  vm.removeRow = function (row) {
    if (!row || row._removeBindingInProgress) return
    if (!authSvc?.hasAccess?.('modifyAssignmentTranscoder')) {
      alertLiteSvc.alertError('Permission denied')
      return
    }

    const serverId = row.serverId
    const bindingId = row.id
    if (!serverId || bindingId == null || bindingId === '') {
      alertLiteSvc.alertError('Setrix server or stream binding id is missing')
      return
    }

    row._removeBindingInProgress = true
    const { networkId, multiorgEnabled } = overviewRequestContext()
    SetrixServer.deleteStreamBinding(serverId, bindingId, networkId, multiorgEnabled)
      .then(() => {
        alertLiteSvc.alert('Stream binding removed successfully', 'success')
        $timeout(() => $scope.$broadcast('refreshTable'))
      })
      .catch((rejection) => {
        alertLiteSvc.alertError(rejection?.data?.message || rejection?.statusText || 'Failed to remove stream binding')
      })
      .finally(() => {
        row._removeBindingInProgress = false
      })
  }

  $scope.$on('$destroy', stopStreamStatusesPolling)

  vm.loadRows = function (tableState) {
    if (!tableState.sort.predicate) {
      tableState.sort.predicate = 'contentName'
      tableState.sort.reverse = false
    }

    const apiParams = buildStreamsOverviewParams(tableState, vm.contentType)

    const { networkId, multiorgEnabled } = overviewRequestContext()
    SetrixServer.loadStreamsOverview(apiParams, networkId, multiorgEnabled)
      .then((response) => {
        const body = response && response.data
        const pagePayload = body && body.payload

        if (!pagePayload || !Array.isArray(pagePayload.content)) {
          vm.rows = []
          vm.isFound = false
          tableState.pagination.start = 0
          tableState.pagination.totalItemCount = 0
          tableState.pagination.numberOfPages = 1
          tableState.pagination.number = tableState.pagination.number || 25
          $scope.loading = false
          stopStreamStatusesPolling()
          return
        }

        vm.rows = pagePayload.content.map(mapOverviewItemToRow)
        vm.isFound = vm.rows.length > 0

        const size = pagePayload.size || (tableState.pagination.number || 25)
        const number = pagePayload.number != null ? pagePayload.number : 0

        tableState.pagination.start = number * size
        tableState.pagination.number = size
        tableState.pagination.totalItemCount = pagePayload.totalElements != null ? pagePayload.totalElements : vm.rows.length
        tableState.pagination.numberOfPages = pagePayload.totalPages != null
          ? pagePayload.totalPages
          : Math.max(1, Math.ceil(tableState.pagination.totalItemCount / size))

        $scope.loading = false

        applyStreamStatusesToRows(vm.rows).finally(() => {
          syncStreamStatusesPollingAfterLoad()
        })
      })
      .catch((rejection) => {
        alertLiteSvc.alertError(rejection?.data?.message || rejection?.statusText || 'Failed to load transcoding status')
        vm.rows = []
        vm.isFound = false
        $scope.loading = false
        stopStreamStatusesPolling()
      })
  }
}
